import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, Clock, AlertCircle,
    FileText, ChevronDown, Loader, Calendar, BarChart2,
    TrendingUp, Eye, ShieldCheck, Download, RotateCcw,
} from "lucide-react";
import {
    fetchAdminInboxRequest,
    fetchAdminHistoryRequest,
    adminDecisionRequest,
} from "../../../../redux/actions/timesheetWorkflowActions";
import {
    fetchTimesheetDaysRequest,
    fetchTimesheetOvertimesRequest,
    fetchOvertimeAdminInboxRequest,
    fetchOvertimeAdminHistoryRequest,
    adminOvertimeDecisionRequest,
} from "../../../../redux/actions/timesheetActions";
import "../TraitementStyles/TraitementFeuillesTemps.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { exportTreatmentExcel } from "./exportTreatmentExcel";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { getRequestTreatedBy } from "./treatmentDecisionInfo";

const TraitementFeuillesTemps = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const { adminInbox = [], adminHistory = [], loading = false } = useSelector(
        (s) => s.timesheetWorkflow || {}
    );
    const { daysByTimesheetId = {}, overtimesByTimesheetId = {}, overtimeAdminInbox = [], overtimeAdminHistory = [] } = useSelector(
        (s) => s.timesheet || {}
    );

    /* ── Local state ─────────────────────────────────────────── */
    const [mainTab, setMainTab] = useState("timesheets"); // "timesheets", "overtimes"
    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");
    const [filters, setFilters] = useState({
        dateDebut: "", dateFin: "", tauxMin: "", modeFilter: "",
    });
    const handleResetFilters = () => {
        setSearchTerm("");
        setFilters({ dateDebut: "", dateFin: "", tauxMin: "", modeFilter: "" });
    };

    /* ── Load ────────────────────────────────────────────────── */
    useEffect(() => {
        dispatch(fetchAdminInboxRequest());
        dispatch(fetchAdminHistoryRequest());
        dispatch(fetchOvertimeAdminInboxRequest());
        dispatch(fetchOvertimeAdminHistoryRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(fetchAdminInboxRequest({ silent: true }));
        dispatch(fetchAdminHistoryRequest({ silent: true }));
        dispatch(fetchOvertimeAdminInboxRequest());
        dispatch(fetchOvertimeAdminHistoryRequest());
    });

    /* ── Helpers ─────────────────────────────────────────────── */
    const fmt = (d) => {
        if (!d) return "N/A";
        return new Date(d).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const mapTab = (s) => {
        if (s === "PENDING_ADMIN") return "enCours";
        if (s === "APPROVED") return "approuve";
        return "enCours";
    };

    const isRejectedByAdmin = (item) =>
        (item?.status || item?.statut) === "REJECTED" && item?.adminDecision === false;

    const statutBadge = (s) => {
        const cfg = {
            PENDING_ADMIN: { label: "En attente de l'admin RH", bg: "#fff3cd", color: "#856404" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Refusé", bg: "#f8d7da", color: "#721c24" },
        };
        const c = cfg[s] || cfg.PENDING_ADMIN;
        return (
            <span className="tftBadge" style={{
                backgroundColor: c.bg, color: c.color
            }}>
                {c.label}
            </span>
        );
    };

    const managerDecisionBadge = (decision) => {
        if (decision === null || decision === undefined) return <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>;
        return decision
            ? <span style={{ color: "#10b981", fontWeight: 600, fontSize: 12 }}>✔ Validé</span>
            : <span style={{ color: "#ef4444", fontWeight: 600, fontSize: 12 }}>✘ Refusé</span>;
    };

    const tauxColor = (t) => {
        if (t >= 90) return "#10b981";
        if (t >= 70) return "#f59e0b";
        return "#ef4444";
    };

    const TauxBar = ({ taux }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 100 }}>
            <div style={{
                flex: 1, height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden",
            }}>
                <div style={{
                    height: "100%", width: `${taux ?? 0}%`,
                    background: tauxColor(taux ?? 0), borderRadius: 3,
                    transition: "width .3s",
                }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: tauxColor(taux ?? 0), minWidth: 32 }}>
                {taux ?? 0}%
            </span>
        </div>
    );

    /* ── Actions ─────────────────────────────────────────────── */
    const handleApprove = (item) => {
        confirmTreatmentApproval(() => {
            if (mainTab === "timesheets") {
                dispatch(adminDecisionRequest({ id: item.id, decision: true, comment: "" }));
            } else {
                dispatch(adminOvertimeDecisionRequest({ overtimeId: item.id, decision: true, comment: "" }));
            }
        });
    };

    const openReject = (item) => { setSelected(item); setShowRejectModal(true); };
    const closeReject = () => { setShowRejectModal(false); setMotifRejet(""); setSelected(null); };
    const confirmReject = () => {
        if (!motifRejet.trim() || !selected) return;
        if (mainTab === "timesheets") {
            dispatch(adminDecisionRequest({ id: selected.id, decision: false, comment: motifRejet }));
        } else {
            dispatch(adminOvertimeDecisionRequest({ overtimeId: selected.id, decision: false, comment: motifRejet }));
        }
        closeReject();
    };

    const openDetails = (item) => {
        setSelected(item);
        const tid = mainTab === "timesheets" ? item.id : item.timesheetId;
        if (tid) {
            dispatch(fetchTimesheetDaysRequest(tid));
            dispatch(fetchTimesheetOvertimesRequest(tid));
        }
        setShowDetailsModal(true);
    };
    const closeDetails = () => { setShowDetailsModal(false); setSelected(null); };

    /* ── Filtrage ────────────────────────────────────────────── */
    const allItems = useMemo(() => {
        if (mainTab === "timesheets") {
            const seen = new Set();
            return [...(adminInbox || []), ...(adminHistory || [])].filter(d => {
                if (seen.has(d.id)) return false;
                seen.add(d.id);
                return true;
            });
        } else {
            const seen = new Set();
            return [...(overtimeAdminInbox || []), ...(overtimeAdminHistory || [])].filter(d => {
                if (seen.has(d.id)) return false;
                seen.add(d.id);
                return true;
            });
        }
    }, [mainTab, adminInbox, adminHistory, overtimeAdminInbox, overtimeAdminHistory]);

    const filtered = useMemo(() => {
        let list = allItems.filter((item) =>
            activeTab === "rejete"
                ? isRejectedByAdmin(item)
                : mapTab(item.status || item.statut) === activeTab
        );
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter(
                (d) =>
                    d.employeeName?.toLowerCase().includes(s) ||
                    d.employeeMatricule?.toLowerCase().includes(s) ||
                    d.periodLabel?.toLowerCase().includes(s) ||
                    d.commentaire?.toLowerCase().includes(s)
            );
        }
        if (filters.dateDebut) list = list.filter((d) => (d.periodStart || d.workDate) >= filters.dateDebut);
        if (filters.dateFin) list = list.filter((d) => (d.periodEnd || d.workDate) <= filters.dateFin);
        if (mainTab === "timesheets" && filters.tauxMin) list = list.filter((d) => (d.tauxRemplissage ?? 0) >= Number(filters.tauxMin));
        return list;
    }, [allItems, activeTab, searchTerm, filters, mainTab]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: allItems.filter(d => (d.status || d.statut) === "PENDING_ADMIN").length },
        { id: "approuve", label: "Approuvés", count: allItems.filter(d => (d.status || d.statut) === "APPROVED").length },
        { id: "rejete", label: "Refusés", count: allItems.filter(isRejectedByAdmin).length },
    ], [allItems]);

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && !adminInbox?.length) {
        return (
            <div className="tftPage">
                <div className="tftWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tftPage">
            <div className="tftWrap">

                {/* Header */}
                <div className="tftHeader">
                    <div>
                        <h1>Traitement des feuilles de temps</h1>
                        <p>Revue finale et approbation des feuilles de temps</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tftCard tftInlineFilters">
                    <div className="tftControls">
                        <div className="tftSearch tftSearchField">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Nom ou matricule..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tftFilterBtn tftFilterToggle" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} /> Filtres
                            <ChevronDown size={16} className={`tftChevron ${showFilters ? "is-open" : ""}`} />
                    </button>
                    <button type="button" className="tftFilterBtn treatmentResetBtn" onClick={handleResetFilters}><RotateCcw size={16} /> Réinitialiser</button>
                    <button type="button" className="tftFilterBtn treatmentExportBtn" onClick={() => exportTreatmentExcel(filtered, "Feuilles_de_temps_admin")}><Download size={16} /> Exporter Excel</button>
                    </div>

                    {(
                        <div className="tftFilters">
                            <div className="tftField">
                                <label>{mainTab === "timesheets" ? "Période début" : "Date début"}</label>
                                <input type="date" value={filters.dateDebut}
                                    onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))} />
                            </div>
                            <div className="tftField">
                                <label>{mainTab === "timesheets" ? "Période fin" : "Date fin"}</label>
                                <input type="date" value={filters.dateFin}
                                    onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))} />
                            </div>
                            {mainTab === "timesheets" && (
                                <div className="tftField">
                                    <label>Taux min (%)</label>
                                    <input type="number" min={0} max={100} value={filters.tauxMin} placeholder="ex: 80"
                                        onChange={e => setFilters(p => ({ ...p, tauxMin: e.target.value }))} />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tftCard tftCard--noPad">
                    <div className="tftTabs tftStatusTabs">
                        {tabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tftTab ${activeTab === tab.id ? "is-active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tftCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="tftTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tftEmpty">
                                <AlertCircle size={46} />
                                <div>Aucun élément trouvé</div>
                            </div>
                        ) : mainTab === "timesheets" ? (
                            <table className="tftTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Période</th>
                                        <th>Heures saisies</th>
                                        <th>Date de soumission</th>
                                        {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((d) => (
                                        <tr key={d.id} onClick={() => openDetails(d)} style={{ cursor: "pointer" }}>
                                            <td><span className="tftPrimary">{d.employeeMatricule || "—"}</span></td>
                                            <td>
                                                <span className="tftPrimary">{d.employeeName}</span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <Calendar size={14} style={{ color: "#6b7280", flexShrink: 0 }} />
                                                    <div className="tftPeriod">
                                                        <div>{fmt(d.periodStart)}</div>
                                                        <div>→ {fmt(d.periodEnd)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    <Clock size={14} style={{ color: "#6b7280" }} />
                                                    <span className="tftPrimary">{d.totalHeures ?? 0}h</span>
                                                </div>
                                            </td>
                                            <td><span className="tftMuted">{fmt(d.dateEnvoi)}</span></td>
                                            {(activeTab === "approuve" || activeTab === "rejete") && (
                                                <td><span className="tftMuted">{getRequestTreatedBy(d)}</span></td>
                                            )}
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                <div className="tftRowActions">
                                                    {d.status === "PENDING_ADMIN" && (
                                                        <>
                                                            <button type="button" className="tftActionBtn tftActionBtn--ok"
                                                                onClick={() => handleApprove(d)} title="Approuver" disabled={loading}>
                                                                <Check size={16} />
                                                            </button>
                                                            <button type="button" className="tftActionBtn tftActionBtn--no"
                                                                onClick={() => openReject(d)} title="Refuser" disabled={loading}>
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button type="button" className="tftActionBtn"
                                                        onClick={() => openDetails(d)} title="Voir les détails"
                                                        style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}>
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <table className="tftTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Date</th>
                                        <th>Plage Horaire</th>
                                        <th>Heures</th>
                                        <th>Projet</th>
                                        <th>Motif / Commentaire</th>
                                        <th>Statut</th>
                                        {(activeTab === "approuve" || activeTab === "rejete") && <th>Traitée par</th>}
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((d) => (
                                        <tr key={d.id} onClick={() => openDetails(d)} style={{ cursor: "pointer" }}>
                                            <td><span className="tftPrimary">{d.employeeMatricule || "—"}</span></td>
                                            <td><span className="tftPrimary">{d.employeeName}</span></td>
                                            <td>{fmt(d.workDate)}</td>
                                            <td>{d.startTime?.substring(0, 5)} - {d.endTime?.substring(0, 5)}</td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                    <Clock size={14} style={{ color: "#6b7280" }} />
                                                    <span className="tftPrimary">{d.heures ?? 0}h</span>
                                                </div>
                                            </td>
                                            <td>{d.projetNom || "—"}</td>
                                            <td>{d.commentaire || "—"}</td>
                                            <td>{statutBadge(d.statut)}</td>
                                            {(activeTab === "approuve" || activeTab === "rejete") && (
                                                <td><span className="tftMuted">{getRequestTreatedBy(d)}</span></td>
                                            )}
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                <div className="tftRowActions">
                                                    {d.statut === "PENDING_ADMIN" && (
                                                        <>
                                                            <button type="button" className="tftActionBtn tftActionBtn--ok"
                                                                onClick={() => handleApprove(d)} title="Approuver" disabled={loading}>
                                                                <Check size={16} />
                                                            </button>
                                                            <button type="button" className="tftActionBtn tftActionBtn--no"
                                                                onClick={() => openReject(d)} title="Refuser" disabled={loading}>
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button type="button" className="tftActionBtn"
                                                        onClick={() => openDetails(d)} title="Voir les détails"
                                                        style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}>
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* ────── MODAL DÉTAILS ────── */}
            {showDetailsModal && selected && (
                <div className="tftModal">
                    <div className="tftModal__panel" role="dialog" aria-modal="true"
                        style={{ maxWidth: 760 }}>
                        <div className="tftModal__header">
                            <div className="tftModal__headLeft">
                                <div className="tftModal__icon">
                                    <ShieldCheck size={18} />
                                </div>
                                <div>
                                    <div className="tftModal__title">
                                        Timesheet #{selected.id} — {selected.employeeName}
                                    </div>
                                    <div className="tftModal__sub">
                                        Soumis le {fmt(selected.dateEnvoi)}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tftIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tftModal__body">
                            <div className="tftGrid2">
                                {/* Collaborateur */}
                                <div className="tftPanel">
                                    <div className="tftPanel__title"><User size={16} /> Collaborateur</div>
                                    <div className="tftKv"><span>Matricule</span><b>{selected.employeeMatricule || "—"}</b></div>
                                    <div className="tftKv"><span>Nom</span><b>{selected.employeeName}</b></div>
                                </div>

                                {/* Période + Mode */}
                                <div className="tftPanel">
                                    <div className="tftPanel__title"><Calendar size={16} /> Période</div>
                                    <div className="tftKv"><span>Début</span><b>{fmt(selected.periodStart)}</b></div>
                                    <div className="tftKv"><span>Fin</span><b>{fmt(selected.periodEnd)}</b></div>
                                    <div className="tftKv"><span>Soumission</span><b>{fmt(selected.dateEnvoi)}</b></div>
                                </div>

                                {/* Métriques */}
                                <div className="tftPanel tftPanel--full" style={{
                                    display: "grid", gridTemplateColumns: "1fr", gap: 16,
                                }}>
                                    <div style={{
                                        background: "#f9fafb", borderRadius: 10, padding: "14px 16px",
                                        display: "flex", flexDirection: "column", gap: 6,
                                    }}>
                                        <div style={{ color: "#3b82f6" }}><Clock size={18} /></div>
                                        <div style={{ fontSize: 22, fontWeight: 700, color: "#3b82f6" }}>{selected.totalHeures ?? 0}h</div>
                                        <div style={{ fontSize: 11, color: "#9ca3af" }}>Heures saisies</div>
                                    </div>
                                </div>

                                 {/* Commentaire employé */}
                                 {selected.commentaireEmploye && (
                                     <div className="tftPanel tftPanel--full">
                                         <div className="tftPanel__title"><FileText size={16} /> Commentaire employé</div>
                                         <div className="tftText">{selected.commentaireEmploye}</div>
                                     </div>
                                 )}

                                 {/* Jours travaillés (Heures Normales) */}
                                 <div className="tftPanel tftPanel--full">
                                     <div className="tftPanel__title"><Clock size={16} /> Heures Normales Saisies</div>
                                     {(!daysByTimesheetId[selected.id] || daysByTimesheetId[selected.id].length === 0) ? (
                                         <p className="tftMuted" style={{ margin: 0, padding: "8px 0" }}>Aucune heure normale.</p>
                                     ) : (
                                         <table className="tftTable" style={{ minWidth: "100%", margin: 0 }}>
                                             <thead>
                                                 <tr>
                                                     <th style={{ padding: "8px" }}>Date</th>
                                                     <th style={{ padding: "8px" }}>Heures</th>
                                                     <th style={{ padding: "8px" }}>Projet</th>
                                                 </tr>
                                             </thead>
                                             <tbody>
                                                 {daysByTimesheetId[selected.id].filter(d => d.heures > 0).map(d => (
                                                     <tr key={d.id}>
                                                         <td style={{ padding: "8px" }}>{new Date(d.workDate).toLocaleDateString("fr-FR")}</td>
                                                         <td style={{ padding: "8px" }}>{d.heures}h</td>
                                                         <td style={{ padding: "8px" }}>{d.projetNom || "—"}</td>
                                                     </tr>
                                                 ))}
                                             </tbody>
                                         </table>
                                     )}
                                 </div>



                                {/* Décision Manager */}
                                {selected.managerDecision !== null && selected.managerDecision !== undefined && (
                                    <div className="tftPanel tftPanel--full">
                                        <div className="tftPanel__title">
                                            <Check size={16} /> Décision Manager
                                        </div>
                                        <div className="tftKv">
                                            <span>Décision</span>
                                            <b style={{ color: selected.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selected.managerDecision ? "Validé" : "Refusé"}
                                            </b>
                                        </div>
                                        {selected.managerName && (
                                            <div className="tftKv"><span>Par</span><b>{selected.managerName}</b></div>
                                        )}
                                        {selected.managerDecisionAt && (
                                            <div className="tftKv"><span>Date</span><b>{fmt(selected.managerDecisionAt)}</b></div>
                                        )}
                                        {selected.managerComment && (
                                            <div className="tftRejectBox">
                                                <div className="tftRejectLabel">Commentaire manager</div>
                                                <div className="tftRejectText">{selected.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Décision RH finale */}
                                {selected.status !== "PENDING_ADMIN" && (
                                    <div className={`tftPanel tftPanel--full ${selected.status === "APPROVED" ? "tftValidOk" : "tftValidNo"}`}>
                                        <div className="tftPanel__title">
                                            {selected.status === "APPROVED"
                                                ? <><Check size={16} /> Timesheet approuvé par RH</>
                                                : <><X size={16} /> Timesheet refusé par RH</>}
                                        </div>
                                        {selected.adminDecisionAt && (
                                            <div className="tftKv"><span>Date</span><b>{fmt(selected.adminDecisionAt)}</b></div>
                                        )}
                                        {selected.adminName && (
                                            <div className="tftKv"><span>Par</span><b>{selected.adminName}</b></div>
                                        )}
                                        {selected.adminComment && (
                                            <div className="tftRejectBox">
                                                <div className="tftRejectLabel">
                                                    {selected.status === "APPROVED" ? "Commentaire" : "Motif du refus"}
                                                </div>
                                                <div className="tftRejectText">{selected.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tftModal__footer">
                            {selected.status === "PENDING_ADMIN" ? (
                                <>
                                    <button className="tftBtn tftBtn--danger"
                                        onClick={() => { closeDetails(); openReject(selected); }} disabled={loading}>
                                        Refuser
                                    </button>
                                    <button className="tftBtn tftBtn--ok"
                                        onClick={() => { handleApprove(selected.id); closeDetails(); }} disabled={loading}>
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                </>
                            ) : null}
                            <button className="tftBtn tftBtn--ghost" onClick={closeDetails}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ────── MODAL REJET ────── */}
            {showRejectModal && selected && (
                <div className="tftModal">
                    <div className="tftModal__panel tftModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tftModal__header">
                            <div className="tftModal__headLeft">
                                <div className="tftModal__icon tftModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tftModal__title">Refuser la feuille de temps</div>
                                    <div className="tftModal__sub">
                                        {selected.employeeName} — {fmt(selected.periodStart)} → {fmt(selected.periodEnd)}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tftIconBtn" onClick={closeReject}><X size={18} /></button>
                        </div>
                        <div className="tftModal__body">
                            <div className="tftMiniCard">
                                <div className="tftMiniRow">
                                    <span className="tftMuted">Heures saisies</span>
                                    <b>{selected.totalHeures ?? 0}h</b>
                                </div>
                                <div className="tftMiniRow">
                                    <span className="tftMuted">Taux remplissage</span>
                                    <b style={{ color: tauxColor(selected.tauxRemplissage ?? 0) }}>
                                        {selected.tauxRemplissage ?? 0}%
                                    </b>
                                </div>
                                {selected.managerDecision !== null && selected.managerDecision !== undefined && (
                                    <div className="tftMiniRow">
                                        <span className="tftMuted">Décision Manager</span>
                                        <b style={{ color: selected.managerDecision ? "#10b981" : "#ef4444" }}>
                                            {selected.managerDecision ? "✔ Validé" : "✘ Refusé"}
                                        </b>
                                    </div>
                                )}
                            </div>
                            <label className="tftLabel">Motif du refus <span className="tftReq">*</span></label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Expliquez pourquoi cette feuille de temps est refusée…"
                                className="tftTextarea"
                            />
                            <div className="tftHelp">Ce commentaire sera visible par le collaborateur et le manager</div>
                        </div>
                        <div className="tftModal__footer">
                            <button type="button" className="tftBtn tftBtn--ghost" onClick={closeReject}>Annuler</button>
                            <button type="button" className="tftBtn tftBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}>
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementFeuillesTemps;
